import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'react-hot-toast';
import { Loader2, ArrowLeft, FileText, Package, CheckCircle2, Clock, XCircle, Send, AlertTriangle, CalendarX, ShoppingCart, DollarSign, Copy, RefreshCw, Edit } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuotationView = () => {
  const { quotationNumber } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [quotation, setQuotation] = useState(null);
  const [validity, setValidity] = useState(null);
  const [rejectionReasons, setRejectionReasons] = useState({});
  
  // Action forms
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [showVersionForm, setShowVersionForm] = useState(false);
  
  const [rejectData, setRejectData] = useState({ rejection_reason: '', rejection_note: '' });
  const [convertData, setConvertData] = useState({
    items_to_convert: [],
    requested_delivery_date: '',
    customer_reference: ''
  });
  const [versionData, setVersionData] = useState({
    valid_from: new Date().toISOString().split('T')[0],
    valid_to: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  const loadQuotation = useCallback(async () => {
    setIsLoading(true);
    try {
      const [qtRes, validityRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/presales/quotations/${quotationNumber}`),
        axios.get(`${BACKEND_URL}/api/presales/quotations/${quotationNumber}/validity`)
      ]);
      setQuotation(qtRes.data);
      setValidity(validityRes.data);
      setConvertData(prev => ({
        ...prev,
        items_to_convert: qtRes.data.items?.map(i => i.item_number) || [],
        customer_reference: qtRes.data.customer_reference || ''
      }));
    } catch (error) {
      console.error('Error loading quotation:', error);
      toast.error('Failed to load quotation');
    } finally {
      setIsLoading(false);
    }
  }, [quotationNumber]);

  const loadRejectionReasons = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/presales/rejection-reasons`);
      setRejectionReasons(response.data);
    } catch (error) {
      console.error('Error loading rejection reasons:', error);
    }
  }, []);

  useEffect(() => {
    loadQuotation();
    loadRejectionReasons();
  }, [loadQuotation, loadRejectionReasons]);

  const handleSend = async () => {
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/presales/quotations/${quotationNumber}/send`);
      setQuotation(response.data);
      toast.success('Quotation marked as sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quotation');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAccept = async () => {
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/presales/quotations/${quotationNumber}/accept`);
      setQuotation(response.data);
      toast.success('Quotation marked as accepted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept quotation');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectData.rejection_reason) {
      toast.error('Select a rejection reason');
      return;
    }
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/presales/quotations/${quotationNumber}/reject`, rejectData);
      setQuotation(response.data);
      toast.success('Quotation rejected');
      setShowRejectForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject quotation');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConvertToOrder = async () => {
    if (convertData.items_to_convert.length === 0) {
      toast.error('Select at least one item to convert');
      return;
    }
    
    // Validate requested delivery date is in the future
    if (convertData.requested_delivery_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const reqDate = new Date(convertData.requested_delivery_date);
      reqDate.setHours(0, 0, 0, 0);
      if (reqDate < today) {
        toast.error('Requested Delivery Date must be today or a future date');
        return;
      }
    }
    
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/presales/quotations/convert-to-order`, {
        quotation_number: quotationNumber,
        ...convertData
      });
      if (response.data.success) {
        toast.success(response.data.message);
        navigate(`/otc/sales-orders/${response.data.target_document}`);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert quotation');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateNewVersion = async () => {
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/presales/quotations/new-version`, {
        quotation_number: quotationNumber,
        ...versionData
      });
      toast.success(`New version ${response.data.quotation_number} created`);
      navigate(`/presales/quotations/${response.data.quotation_number}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create new version');
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleItemSelection = (itemNumber) => {
    setConvertData(prev => ({
      ...prev,
      items_to_convert: prev.items_to_convert.includes(itemNumber)
        ? prev.items_to_convert.filter(n => n !== itemNumber)
        : [...prev.items_to_convert, itemNumber]
    }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      'DRAFT': { color: 'bg-gray-100 text-gray-800', icon: FileText },
      'SENT': { color: 'bg-orange-100 text-orange-800', icon: Send },
      'ACCEPTED': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      'CONVERTED': { color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
      'REJECTED': { color: 'bg-red-100 text-red-800', icon: XCircle },
      'EXPIRED': { color: 'bg-orange-100 text-orange-800', icon: CalendarX }
    };
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', icon: FileText };
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${badge.color}`}>
        <Icon className="w-4 h-4" /> {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3">Loading quotation...</span>
        </div>
      </Layout>
    );
  }

  if (!quotation) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Quotation Not Found</h1>
          <Button onClick={() => navigate('/presales/quotations')}>Back to Quotations</Button>
        </div>
      </Layout>
    );
  }

  const canSend = quotation.status === 'DRAFT';
  const canAccept = ['DRAFT', 'SENT'].includes(quotation.status);
  const canReject = ['DRAFT', 'SENT'].includes(quotation.status);
  const canConvert = ['DRAFT', 'SENT', 'ACCEPTED'].includes(quotation.status) && validity?.is_valid;
  const canCreateVersion = !['CONVERTED'].includes(quotation.status);
  const canEdit = ['DRAFT', 'SENT'].includes(quotation.status);

  return (
    <Layout>
      <div className="p-6" data-testid="quotation-view">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/presales/quotations')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6" />
                Quotation {quotation.quotation_number}
              </h1>
              <p className="text-sm text-gray-600">
                Version {quotation.version} | Document Type: {quotation.document_type}
                {quotation.previous_version && (
                  <span className="ml-2">
                    (Previous: <button onClick={() => navigate(`/presales/quotations/${quotation.previous_version}`)} className="text-orange-600 hover:underline">{quotation.previous_version}</button>)
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(quotation.status)}
          </div>
        </div>

        {/* Validity Warning */}
        {validity && !validity.is_valid && quotation.status !== 'CONVERTED' && quotation.status !== 'REJECTED' && (
          <Card className="p-4 mb-6 bg-orange-50 border-orange-200">
            <div className="flex items-center gap-2 text-orange-800">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">{validity.message}</span>
            </div>
          </Card>
        )}

        {/* Status Flow & Actions */}
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                ['DRAFT', 'SENT', 'ACCEPTED', 'CONVERTED'].includes(quotation.status) ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>1</span>
              <span className="text-sm font-medium">Draft</span>
              <span className="mx-2 text-gray-300">→</span>
              
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                ['SENT', 'ACCEPTED', 'CONVERTED'].includes(quotation.status) ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>2</span>
              <span className="text-sm font-medium">Sent</span>
              <span className="mx-2 text-gray-300">→</span>
              
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                ['ACCEPTED', 'CONVERTED'].includes(quotation.status) ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>3</span>
              <span className="text-sm font-medium">Accepted</span>
              <span className="mx-2 text-gray-300">→</span>
              
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                quotation.status === 'CONVERTED' ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>4</span>
              <span className="text-sm font-medium">Sales Order</span>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              {canEdit && (
                <Button variant="outline" onClick={() => navigate(`/presales/quotations/${quotationNumber}/edit`)} data-testid="edit-quotation-btn">
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </Button>
              )}
              {canSend && (
                <Button variant="outline" onClick={handleSend} disabled={isProcessing}>
                  <Send className="w-4 h-4 mr-2" /> Send to Customer
                </Button>
              )}
              {canAccept && (
                <Button variant="outline" onClick={handleAccept} disabled={isProcessing}>
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Accepted
                </Button>
              )}
              {canReject && (
                <Button variant="outline" className="text-red-600" onClick={() => setShowRejectForm(true)} disabled={isProcessing}>
                  <XCircle className="w-4 h-4 mr-2" /> Reject
                </Button>
              )}
              {canConvert && (
                <Button onClick={() => setShowConvertForm(true)} disabled={isProcessing} data-testid="convert-to-order-btn">
                  <ShoppingCart className="w-4 h-4 mr-2" /> Convert to Sales Order
                </Button>
              )}
              {canCreateVersion && (
                <Button variant="outline" onClick={() => setShowVersionForm(true)} disabled={isProcessing}>
                  <Copy className="w-4 h-4 mr-2" /> New Version
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Reject Form */}
        {showRejectForm && (
          <Card className="p-6 mb-6 border-2 border-red-200 bg-red-50">
            <h3 className="text-lg font-semibold mb-4 text-red-800">Reject Quotation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label>Rejection Reason *</Label>
                <Select value={rejectData.rejection_reason || '__SELECT__'} onValueChange={(v) => setRejectData({...rejectData, rejection_reason: v === '__SELECT__' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Select Reason" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__SELECT__">Select Reason</SelectItem>
                    {Object.entries(rejectionReasons).map(([code, desc]) => (
                      <SelectItem key={code} value={code}>{code} - {desc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note</Label>
                <Input
                  value={rejectData.rejection_note}
                  onChange={(e) => setRejectData({...rejectData, rejection_note: e.target.value})}
                  placeholder="Optional note"
                  maxLength={500}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="destructive" onClick={handleReject} disabled={isProcessing}>
                {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                Confirm Rejection
              </Button>
              <Button variant="outline" onClick={() => setShowRejectForm(false)}>Cancel</Button>
            </div>
          </Card>
        )}

        {/* Convert to Order Form */}
        {showConvertForm && (
          <Card className="p-6 mb-6 border-2 border-green-200 bg-green-50">
            <h3 className="text-lg font-semibold mb-4 text-green-800">Convert to Sales Order</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label>Requested Delivery Date *</Label>
                <Input
                  type="date"
                  value={convertData.requested_delivery_date}
                  onChange={(e) => setConvertData({...convertData, requested_delivery_date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-gray-600 mt-1">Must be today or a future date</p>
              </div>
              <div>
                <Label>Customer Reference</Label>
                <Input
                  value={convertData.customer_reference}
                  onChange={(e) => setConvertData({...convertData, customer_reference: e.target.value})}
                  placeholder="PO Number"
                />
              </div>
            </div>
            <div className="mb-4">
              <Label>Select Items to Convert</Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {quotation.items?.map((item) => {
                  const isConverted = item.converted_quantity >= item.quoted_quantity;
                  return (
                    <label key={item.item_number} className={`flex items-center gap-3 p-2 rounded ${isConverted ? 'bg-gray-100 opacity-50' : 'bg-white'}`}>
                      <input
                        type="checkbox"
                        checked={convertData.items_to_convert.includes(item.item_number)}
                        onChange={() => toggleItemSelection(item.item_number)}
                        disabled={isConverted}
                        className="w-4 h-4"
                      />
                      <span className="font-mono">{item.item_number.toString().padStart(4, '0')}</span>
                      <span className="flex-1">{item.item_description}</span>
                      <span className="font-mono">{item.quoted_quantity} {item.unit_of_measure}</span>
                      <span className="font-mono text-green-600">${item.gross_value?.toFixed(2)}</span>
                      {isConverted && <span className="text-xs text-green-600">Already converted</span>}
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleConvertToOrder} disabled={isProcessing}>
                {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
                Create Sales Order
              </Button>
              <Button variant="outline" onClick={() => setShowConvertForm(false)}>Cancel</Button>
            </div>
          </Card>
        )}

        {/* New Version Form */}
        {showVersionForm && (
          <Card className="p-6 mb-6 border-2 border-orange-200 bg-orange-50">
            <h3 className="text-lg font-semibold mb-4 text-orange-800">Create New Version</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label>Valid From *</Label>
                <Input
                  type="date"
                  value={versionData.valid_from}
                  onChange={(e) => setVersionData({...versionData, valid_from: e.target.value})}
                />
              </div>
              <div>
                <Label>Valid To *</Label>
                <Input
                  type="date"
                  value={versionData.valid_to}
                  onChange={(e) => setVersionData({...versionData, valid_to: e.target.value})}
                />
              </div>
            </div>
            <p className="text-sm text-orange-700 mb-4">
              A new quotation will be created with updated validity dates. Pricing will be recalculated.
            </p>
            <div className="flex gap-2">
              <Button onClick={handleCreateNewVersion} disabled={isProcessing}>
                {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
                Create New Version
              </Button>
              <Button variant="outline" onClick={() => setShowVersionForm(false)}>Cancel</Button>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Customer Info */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Customer Information</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Customer</span>
                <span className="font-medium">{quotation.customer_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Name</span>
                <span className="font-medium">{quotation.customer_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Sales Org</span>
                <span className="font-medium">{quotation.sales_organization}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Dist. Channel</span>
                <span className="font-medium">{quotation.distribution_channel}</span>
              </div>
              {quotation.division && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Division</span>
                  <span className="font-medium">{quotation.division}</span>
                </div>
              )}
              {quotation.payment_terms && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Terms</span>
                  <span className="font-medium">{quotation.payment_terms}</span>
                </div>
              )}
              {quotation.pricing_procedure && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Pricing Procedure</span>
                  <span className="font-medium text-orange-600">{quotation.pricing_procedure}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Dates & Validity */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Dates & Validity</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Quotation Date</span>
                <span className="font-medium">{quotation.quotation_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Valid From</span>
                <span className="font-medium">{quotation.valid_from}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Valid To</span>
                <span className="font-medium">{quotation.valid_to}</span>
              </div>
              {quotation.requested_delivery_date && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Requested Delivery Date</span>
                  <span className="font-medium text-orange-600">{quotation.requested_delivery_date}</span>
                </div>
              )}
              {validity && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Days Remaining</span>
                  <span className={`font-medium ${validity.days_remaining > 7 ? 'text-green-600' : validity.days_remaining > 0 ? 'text-orange-600' : 'text-red-600'}`}>
                    {validity.days_remaining > 0 ? `${validity.days_remaining} days` : 'Expired'}
                  </span>
                </div>
              )}
              {quotation.sent_date && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Sent Date</span>
                  <span className="font-medium">{quotation.sent_date}</span>
                </div>
              )}
              {quotation.accepted_date && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Accepted Date</span>
                  <span className="font-medium">{quotation.accepted_date}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Values */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" /> Quotation Values
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Net Value</span>
                <span className="font-mono font-medium text-lg">
                  ${quotation.net_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-700">Tax Amount</span>
                <span className="font-mono">
                  ${quotation.tax_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-100 rounded-lg">
                <span className="text-orange-900 font-semibold">Gross Value</span>
                <span className="font-mono font-bold text-xl text-orange-700">
                  ${quotation.gross_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Currency</span>
                <span className="font-medium">{quotation.currency}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Rejection Info */}
        {quotation.status === 'REJECTED' && (
          <Card className="p-6 mb-6 bg-red-50 border-red-200">
            <h3 className="text-lg font-semibold mb-2 text-red-800">Rejection Details</h3>
            <div className="space-y-2">
              <p><span className="text-gray-600">Reason:</span> <span className="font-medium">{quotation.rejection_reason} - {rejectionReasons[quotation.rejection_reason]}</span></p>
              {quotation.rejection_note && <p><span className="text-gray-600">Note:</span> {quotation.rejection_note}</p>}
              <p><span className="text-gray-600">Rejected Date:</span> {quotation.rejected_date}</p>
            </div>
          </Card>
        )}

        {/* Document Flow */}
        {(quotation.reference_inquiry_number || quotation.sales_order_numbers?.length > 0) && (
          <Card className="p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Document Flow</h3>
            <div className="flex items-center gap-4 flex-wrap">
              {quotation.reference_inquiry_number && (
                <>
                  <button
                    onClick={() => navigate(`/presales/inquiries/${quotation.reference_inquiry_number}`)}
                    className="px-4 py-2 bg-orange-100 text-orange-800 rounded-lg hover:bg-orange-200"
                  >
                    Inquiry: {quotation.reference_inquiry_number}
                  </button>
                  <span className="text-gray-400">→</span>
                </>
              )}
              <span className="px-4 py-2 bg-orange-100 text-orange-800 rounded-lg font-medium">
                Quotation: {quotation.quotation_number}
              </span>
              {quotation.sales_order_numbers?.length > 0 && (
                <>
                  <span className="text-gray-400">→</span>
                  {quotation.sales_order_numbers.map(so => (
                    <button
                      key={so}
                      onClick={() => navigate(`/otc/sales-orders/${so}`)}
                      className="px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200"
                    >
                      Sales Order: {so}
                    </button>
                  ))}
                </>
              )}
            </div>
          </Card>
        )}

        {/* Items Table */}
        <Card className="overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Package className="w-5 h-5" /> Quotation Items
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Material</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Qty</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Net Price</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Net Value</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Tax</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Gross Value</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Converted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {quotation.items?.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono">{item.item_number.toString().padStart(4, '0')}</td>
                    <td className="px-4 py-3 font-mono text-sm">{item.material_id || '-'}</td>
                    <td className="px-4 py-3">{item.item_description}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {item.quoted_quantity} {item.unit_of_measure}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      ${item.net_price?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      ${item.net_value?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      ${item.tax_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">
                      ${item.gross_value?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.converted_to_order ? (
                        <button
                          onClick={() => navigate(`/otc/sales-orders/${item.converted_to_order}`)}
                          className="text-green-600 hover:underline text-sm"
                        >
                          {item.converted_to_order}
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100 font-semibold">
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-right">Totals:</td>
                  <td className="px-4 py-3 text-right font-mono">
                    ${quotation.net_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    ${quotation.tax_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-orange-600">
                    ${quotation.gross_value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>

        {/* Notes & Timestamps */}
        {quotation.header_note && (
          <Card className="p-6 mt-6">
            <h3 className="text-lg font-semibold mb-2">Notes</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{quotation.header_note}</p>
          </Card>
        )}

        <div className="mt-6 text-sm text-gray-500">
          <p>Created: {quotation.created_at ? new Date(quotation.created_at).toLocaleString() : '-'}</p>
          <p>Updated: {quotation.updated_at ? new Date(quotation.updated_at).toLocaleString() : '-'}</p>
        </div>
      </div>
    </Layout>
  );
};

export default QuotationView;
